import * as React from 'react'
import { useCallback, useEffect, useState } from 'react'
import { useToasts } from 'react-toast-notifications'

// routing
import { RouteComponentProps } from 'react-router'
import { Link } from 'react-router-dom'

// libraries
import {
  RedditIcon,
  RedditShareButton,
  TelegramIcon,
  TelegramShareButton,
  TwitterIcon,
  TwitterShareButton,
} from 'react-share'

// Helpers
import { connectWalletWithProvider, NETWORK } from '../poap-eth'
import {
  getMoments,
  getTokenInfoWithENS,
  getTransactionRequest,
  postTokenFreeze,
  postTokenMigration,
  TokenInfo,
  TransactionRequestStatus,
} from '../api'
import { useInterval } from 'lib/hooks/useInterval'

// constants
import { LAYERS, MOMENTS_APP_BASE_URL, TWITTER_SHARE_BASE_LINK } from '../lib/constants'

import chevronLeft from 'images/chevron-left.svg'
import { useLedgerContext } from 'ScanPage/LedgerContext'

// assets
import EmptyBadge from '../images/empty-badge.svg'
import HeaderShadowImg from '../images/header-shadow.svg'
import HeaderShadowDesktopImg from '../images/header-shadow-desktop.svg'
import RedirectionImg from '../images/redirection.svg'
import { ReactComponent as CalendarIcon } from 'images/icons/calendar.svg'
import { ReactComponent as GlobeIcon } from 'images/icons/globe.svg'
import { ReactComponent as LocationIcon } from 'images/icons/location.svg'

// utils
import { useBodyClassName } from '../react-helpers'
import { SubmitButton } from '../components/SubmitButton'
import abi from '../abis/PoapDelegatedMint.json'
import { TransactionReceipt } from 'web3-core'
import { TxDetail } from '../components/TxDetail'
import { usePrivyWrapper } from '../hooks/usePrivyWrapper'
import Web3 from 'web3'
import ExpandableImageContainer from 'CodeClaimPage/components/ExpandableImageContainer'

const CONTRACT_ADDRESS = process.env.REACT_APP_MINT_DELEGATE_CONTRACT
const poapGalleryUrl = (eventId: number) => {
  return `${process.env.REACT_APP_GALLERY_DOMAIN}/event/${eventId}`
}

const poapFamilyUrl = (eventId: number) => {
  return `${process.env.REACT_APP_FAMILY_DOMAIN}/event/${eventId}`
}

interface MigrationParams {
  signature: string
  expirationTime: number
}

export const TokenDetailPage: React.FC<
  RouteComponentProps<{
    tokenId: string
  }>
> = ({ match }) => {
  const [token, setToken] = useState<null | TokenInfo>(null)
  const [web3, setWeb3] = useState<any>(null)
  const [migrateInProcess, setMigrateInProcess] = useState<boolean>(false)
  const [migrationFinished, setMigrationFinished] = useState<boolean>(false)
  const [moments, setMoments] = useState<number>(0)
  const [isFrozen, setIsFrozen] = useState<boolean>(false)
  const [txHash, setTxHash] = useState<string>('')
  const [txReceipt, setTxReceipt] = useState<null | TransactionReceipt>(null)
  const [transactionRequestId, setTransactionRequestId] = useState<number | undefined>(undefined)
  const [descriptionClass, setDescriptionClass] = useState('short-description-data')
  const [migrationParams, setMigrationParams] = useState<MigrationParams | null>(null)
  const { connectWallet: connectWalletWithPrivy, recentConnectedWallet } = usePrivyWrapper()

  const { addToast } = useToasts()
  const { ledger } = useLedgerContext()

  const showShortDescription = () => {
    setDescriptionClass('short-description-data')
  }

  const showFullDescription = () => {
    setDescriptionClass('full-description-data')
  }

  const submitMigration = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    if (!token) return
    setMigrateInProcess(true)
    postTokenFreeze(parseInt(token.tokenId))
      .then((result) => {
        if (result) {
          setTransactionRequestId(result.transactionRequestId)
        }
      })
      .catch((err) => {
        setIsFrozen(true)
      })
  }

  const connectWalletForMigration = async (signature: string, expirationTime: number) => {
    setMigrationParams({ signature, expirationTime })
    if (ledger?.provider) {
      const response = await connectWalletWithProvider({ provider: ledger.provider })
      if (!response.web3) return
      if (response.networkError) {
        let message = `Wrong network, please connect to ${NETWORK}.`
        addToast(message, {
          appearance: 'error',
          autoDismiss: false,
        })
        return
      }
      setWeb3(response.web3)
    } else {
      connectWalletWithPrivy()
    }
  }

  const showErrorMessage = (err?: string) => {
    setMigrateInProcess(false)

    const errorMessage = 'Sorry, the migration had an error, please try again later'

    addToast(errorMessage, {
      appearance: 'error',
      autoDismiss: false,
    })
  }

  const migrateToken = useCallback(
    async (signature: string, expirationTime: number) => {
      if (!web3) return null

      const accounts = await web3.eth.getAccounts()
      if (accounts.length === 0) return null

      const account = accounts[0]
      if (!token) return

      try {
        const contract = new web3.eth.Contract(abi, CONTRACT_ADDRESS)
        let fee = await contract.methods.migrationFee().call()
        let gas = 1000000
        try {
          gas = await contract.methods
            .mintToken(token.event.id, token.tokenId, token.owner, expirationTime, signature)
            .estimateGas({ from: account })
          gas = Math.floor(gas * 1.3)
        } catch (e) {
          gas = Math.floor(gas * 1.3)
        }

        contract.methods
          .mintToken(token.event.id, token.tokenId, token.owner, expirationTime, signature)
          .send({ from: account, gas: gas, value: fee }, (err: any, hash: string | null) => {
            if (err) {
              setIsFrozen(false)
              setTransactionRequestId(undefined)
              setMigrateInProcess(false)
              showErrorMessage()
            }
            if (hash) {
              setTxHash(hash)
            }
          })
      } catch (e) {
        setIsFrozen(false)
        setTransactionRequestId(undefined)
        setMigrateInProcess(false)
        showErrorMessage()
      }
    },
    [web3, token] /* eslint-disable-line react-hooks/exhaustive-deps */,
  )

  const getReceipt = async () => {
    let receipt: null | TransactionReceipt = null
    if (web3 && txHash !== '' && !txReceipt) {
      receipt = await web3.eth.getTransactionReceipt(txHash)
      if (receipt) {
        setTxReceipt(receipt)
      }
    }

    if (!receipt || !receipt.status || (txReceipt && !txReceipt.status)) {
      setMigrateInProcess(false)
    }

    if (receipt && receipt.status) setMigrationFinished(true)
  }

  useBodyClassName('poap-app event-page')

  useEffect(() => {
    if (web3 && migrationParams) {
      migrateToken(migrationParams.signature, migrationParams.expirationTime)
    }
  }, [migrationParams, web3, migrateToken])

  useEffect(() => {
    if (recentConnectedWallet && migrateInProcess) {
      recentConnectedWallet.getWeb3jsProvider().then((provider) => {
        const _web3 = new Web3(provider)
        _web3.eth.net.getNetworkType().then((connectedNetwork) => {
          const networkError = connectedNetwork && NETWORK.toLowerCase().indexOf(connectedNetwork.toLowerCase()) === -1
          if (networkError) {
            let message = `Wrong network, please connect to ${NETWORK}.`
            addToast(message, {
              appearance: 'error',
              autoDismiss: false,
            })
            setIsFrozen(false)
            setTransactionRequestId(undefined)
            setMigrateInProcess(false)
          } else {
            setWeb3(_web3)
          }
        })
      })
    }
  }, [setWeb3, recentConnectedWallet, migrateInProcess])

  useEffect(() => {
    const fetchENS = async () => {
      const token = await getTokenInfoWithENS(match.params.tokenId)
      setToken(token)
      showShortDescription()
    }
    fetchENS()
  }, [match])

  useEffect(() => {
    const fetchMoments = async (id: number) => {
      try {
        const res = await getMoments(id)
        if (res && res.total) {
          setMoments(res.total)
        }
      } catch (e) {
        console.log(e)
      }
    }

    if (token) {
      fetchMoments(token.event.id)
    }
  }, [token])

  useEffect(() => {
    if (txHash && web3) {
      const interval = setInterval(() => {
        getReceipt()
      }, 3000)
      return () => clearInterval(interval)
    }
  }, [txHash, txReceipt]) /* eslint-disable-line react-hooks/exhaustive-deps */

  useInterval(() => {
    if (transactionRequestId && !isFrozen && migrateInProcess) {
      const fetchTransactionRequest = async () => {
        const transactionRequest = await getTransactionRequest(transactionRequestId)
        if (transactionRequest.status === TransactionRequestStatus.finish && transactionRequest.result) {
          setIsFrozen(true)
        }
        if (transactionRequest.status === TransactionRequestStatus.finish_with_error) {
          showErrorMessage('Something went wrong try again later')
        }
      }
      void fetchTransactionRequest()
    }
  }, 3000)

  useEffect(() => {
    if (isFrozen && token) {
      setTimeout(function () {
        postTokenMigration(parseInt(token.tokenId))
          .then((result) => {
            if (result) {
              connectWalletForMigration(result.signature, result.expirationTime)
            }
          })
          .catch((err) => {
            setTransactionRequestId(undefined)
            setIsFrozen(false)
            showErrorMessage('Error while trying to submit transaction.\nPlease try again in a few minutes')
          })
      }, 5000)
    }
  }, [isFrozen]) /* eslint-disable-line react-hooks/exhaustive-deps */

  if (token == null) {
    return (
      <>
        <div className="header-events token-page">
          <div className="container">
            <h1>Loading</h1>
            <div className="logo-event token-page">
              <img src={EmptyBadge} alt="Event" />
            </div>
          </div>
        </div>
        <main id="site-main" role="main" className="main-events">
          <div className="image-main">
            <img src={HeaderShadowImg} alt="" className="mobile" />
            <img src={HeaderShadowDesktopImg} alt="" className="desktop" />
          </div>
          <div className="main-content">
            <div className="container">
              <div className="content-event">
                <h2>Collection</h2>
              </div>
            </div>
          </div>
        </main>
      </>
    )
  }

  const { event, owner, layer, ens, supply, tokenId, created } = token
  let address = owner

  if (ens && ens.valid) {
    address = ens.ens
  }

  const date = new Date(created.replace(' ', 'T')).toLocaleString('en-us', {
    month: 'short',
    day: '2-digit',
    year: 'numeric',
  })

  const tooltipText = `POAP #${supply.order} of ${supply.total} for this drop. 🥳`

  return (
    <>
      <div className="header-events token-page">
        <div className="container">
          <Link to={`/scan/${address}`} className="back-buttom">
            <img src={chevronLeft} alt="Back to admin" />
            <strong>Back</strong>
          </Link>
        </div>
        <div className="container">
          <h1>{event.name}</h1>
          <p>
            {(event.city || event.country) && (
              <>
                {event.city ? `${event.city}, ` : ''}
                {event.country}
                <br />
              </>
            )}
            <b>{event.start_date}</b>
          </p>
          {/* TODO: original image */}
          <div className="logo-event-wrapper">
            <ExpandableImageContainer className="logo-event" src={event.image_url} alt="Event">
              {typeof event.image_url === 'string' && <img src={event.image_url} alt="Event" />}
            </ExpandableImageContainer>
            {!!moments && (
              <div className="we-look-container">
                <a
                  className="we-look"
                  aria-label={'View Moments'}
                  data-cooltipz-dir="top"
                  data-cooltipz-custom="token"
                  href={MOMENTS_APP_BASE_URL + 'drop/' + event.id}
                  target="_blank"
                >
                  <svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path
                      d="M9.98018 1.057H8.9703C8.61382 0.746114 8.19802 0.559586 7.72275 0.559586H6.23762C5.88118 0.248705 5.46535 0 4.9307 0H1.90099C0.831683 0.0621762 0 0.93264 0 1.98964V10.0103C0 11.0674 0.831683 12 1.90099 12H5.0495C5.52476 12 6 11.8135 6.35644 11.4404H7.84155C8.31682 11.4404 8.7327 11.2539 9.0891 10.943H10.099C11.1089 10.943 12 10.0726 12 8.95335V3.04664C11.8812 1.98964 11.0495 1.057 9.98018 1.057ZM10.7524 9.01553C10.7524 9.45075 10.3961 9.8238 9.98018 9.8238H9.62378C9.62378 9.69945 9.68318 9.63727 9.68318 9.51293V2.54922C9.68318 2.42487 9.68318 2.36269 9.62378 2.23835H9.98018C10.3961 2.23835 10.7524 2.6114 10.7524 3.04664V9.01553ZM1.12871 10.0726V1.98964C1.12871 1.5544 1.48515 1.18135 1.90099 1.18135H3.86138H4.9307H5.0495C5.40594 1.18135 5.64356 1.43005 5.76238 1.74093C5.76238 1.8031 5.82178 1.92746 5.82178 1.98964V10.0103C5.82178 10.0726 5.82178 10.1969 5.76238 10.2591C5.64356 10.57 5.40594 10.8187 5.0495 10.8187H4.87129H3.86138H1.90099C1.42574 10.8187 1.12871 10.5078 1.12871 10.0726ZM6.89109 10.0726V9.88597V2.17616V1.98964C6.89109 1.92746 6.89109 1.8031 6.89109 1.74093H7.48515H7.78215C8.07922 1.74093 8.37623 1.92746 8.49503 2.23835C8.55443 2.36269 8.55443 2.42487 8.55443 2.54922V9.51293C8.55443 9.63727 8.55443 9.76163 8.49503 9.8238C8.37623 10.1348 8.07922 10.3213 7.78215 10.3213H7.48515H6.89109C6.89109 10.1969 6.89109 10.1348 6.89109 10.0726Z"
                      fill="white"
                    />
                  </svg>
                  <div className="total-moments">{moments > 99 ? '+99' : moments}</div>
                </a>
              </div>
            )}
          </div>
        </div>
      </div>
      <main id="site-main" role="main" className="main-events">
        <div className="image-main">
          <img src={HeaderShadowImg} alt="" className="mobile" />
          <img src={HeaderShadowDesktopImg} alt="" className="desktop" />
        </div>
        <div className="main-content">
          <div className="container claim-info">
            <div className="content-event">
              <div className="token-meta-info">
                <div
                  className="supply-number"
                  aria-label={tooltipText}
                  data-cooltipz-dir="top"
                  data-cooltipz-custom="token"
                >
                  {supply.order + ' / ' + supply.total}
                </div>
                <div className="token-id">{'#' + tokenId}</div>
                <div className="mint-date">Minted on {date}</div>
              </div>
              <div className="externalLinks">
                <a className="link" href={poapGalleryUrl(event.id)} target={'_blank'} rel="noopener noreferrer">
                  <img src={RedirectionImg} alt="" />
                  View in Gallery
                </a>
                <a className="link" href={poapFamilyUrl(event.id)} target={'_blank'} rel="noopener noreferrer">
                  <img src={RedirectionImg} alt="" />
                  View in Family
                </a>
              </div>
              <p className={descriptionClass}>{event.description}</p>
              <a
                onClick={showFullDescription}
                className={descriptionClass === 'short-description-data' ? '' : 'd-none'}
              >
                more
              </a>
              <ul className="token-data">
                {event.city && (
                  <li>
                    <LocationIcon />
                    {event.city}
                  </li>
                )}
                <li>
                  <CalendarIcon />
                  <span>{event.start_date}</span>
                </li>
                {event.event_url && (
                  <li>
                    <GlobeIcon />
                    <a href={event.event_url} target={'_blank'} rel="noopener noreferrer">
                      Website
                    </a>
                  </li>
                )}
              </ul>
              <h2>Collected by:</h2>
              <p className={`wallet-number ${ens && ens.valid ? 'ens' : ''}`}>
                <Link to={`/scan/${address}`}>{address}</Link>
              </p>
              <h2>Tell your friends!</h2>
              <ul className="social-icons">
                <li>
                  <TwitterShareButton
                    url={window.location.toString()}
                    title={`Look at my ${event.name} POAP NFT!`}
                    via="poapxyz"
                    openWindow={false}
                    onClick={(link: string) => {
                      const queryParams = link.split('?')[1]
                      const twitterShareLink = TWITTER_SHARE_BASE_LINK + '?' + queryParams
                      window.open(twitterShareLink)
                    }}
                  >
                    <TwitterIcon size={40} round iconBgStyle={{ fill: '#6534FF' }} />
                  </TwitterShareButton>
                </li>
                <li>
                  <TelegramShareButton url={window.location.toString()} title={`Look at my ${event.name} POAP NFT!`}>
                    <TelegramIcon size={40} round iconBgStyle={{ fill: '#6534FF' }} />
                  </TelegramShareButton>
                </li>
                <li>
                  <RedditShareButton url={window.location.toString()} title={`Look at my ${event.name} POAP NFT!`}>
                    <RedditIcon size={40} round iconBgStyle={{ fill: '#6534FF' }} />
                  </RedditShareButton>
                </li>
              </ul>
            </div>
            <div className={'migration-section'}>
              {layer === LAYERS.layer2 && !migrationFinished && !txHash && (
                <>
                  <div className={'divider'} />
                  <p>
                    The POAP is currently on Gnosis and it can be{' '}
                    <a href="https://poap.zendesk.com/hc/en-us/articles/9673605937549-How-Do-I-Migrate-My-POAP-to-Mainnet-">
                      migrated to mainnet
                    </a>
                    .
                  </p>
                  <div>
                    <form onSubmit={submitMigration}>
                      <SubmitButton text={'Migrate POAP'} isSubmitting={migrateInProcess} canSubmit={true} />
                    </form>
                  </div>
                </>
              )}
              {layer === LAYERS.layer2 && migrationFinished && <p className={'success'}>POAP migrated successfully!</p>}
              {txHash && <TxDetail hash={txHash} status={'passed'} chain_id={1} />}
              {txReceipt && !txReceipt.status && (
                <>
                  <div className={'divider'} />
                  <div className={'text-info'} data-aos="fade-up">
                    <p>It seems that your transaction failed. Please refresh the page</p>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      </main>
    </>
  )
}
